// ---------------------------------------------------------------------------
// InsertPanel — 2-tier block/element creation panel.
//
// Structure:
//   1. FROM LIBRARY — reuse existing blocks from other pages
//   2. CREATE MANUALLY — new empty block + categorized element catalog
//
// Search filters across all sections.
// ---------------------------------------------------------------------------

import { useState, useMemo, useRef, useEffect } from 'react'
import { useStudioLocale } from '@/i18n'
import { useEditorStore } from '@/features/page-editor/store/editor-store'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import { cn } from '@/lib/utils'
import {
  Search,
  Square,
  Columns2,
  Grid2x2,
  CreditCard,
  Minus,
  Type,
  Heading,
  Image,
  Star,
  Tag,
  MousePointerClick,
  TextCursorInput,
  AlignLeft,
  CheckSquare,
  ChevronsUpDown,
  List,
  Table,
  Plus,
  Box,
  ChevronRight,
  Library,
  Wrench,
  // New icons for expanded palette
  PanelTop,
  Layers,
  PanelLeft,
  CircleUser,
  Link,
  Text,
  ToggleLeft,
  SlidersHorizontal,
  CircleDot,
  Calendar,
  FileInput,
  ChevronsDown,
  GalleryHorizontal,
  UnfoldVertical,
  ToggleRight,
  Menu,
  AppWindow,
  AlertTriangle,
  MessageSquare,
  Info,
  Loader,
  BarChart3,
} from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { createElement, isContainer, PALETTE_CATEGORIES } from '@/features/page-editor/lib/element-factory'
import { findElementInTree, findParentInTree } from '@/features/page-editor/store/canvas-slice'
import { toast } from '@/store/toast-store'
import { canAddChild, getMaxChildren } from '@/features/page-editor/lib/bounded-flex'

// ---------------------------------------------------------------------------
// Icon map — maps palette icon names to lucide-react components
// ---------------------------------------------------------------------------

const ICON_MAP: Record<string, LucideIcon> = {
  'square': Square,
  'columns-2': Columns2,
  'grid-2x2': Grid2x2,
  'credit-card': CreditCard,
  'minus': Minus,
  'type': Type,
  'heading': Heading,
  'image': Image,
  'star': Star,
  'tag': Tag,
  'mouse-pointer-click': MousePointerClick,
  'text-cursor-input': TextCursorInput,
  'align-left': AlignLeft,
  'check-square': CheckSquare,
  'chevrons-up-down': ChevronsUpDown,
  'list': List,
  'table': Table,
  // Layout
  'panel-top': PanelTop,
  'layers': Layers,
  'panel-left': PanelLeft,
  // Content
  'circle-user': CircleUser,
  'link': Link,
  'text': Text,
  // Form
  'toggle-left': ToggleLeft,
  'sliders-horizontal': SlidersHorizontal,
  'circle-dot': CircleDot,
  'calendar': Calendar,
  'file-input': FileInput,
  // Interactive
  'chevrons-down': ChevronsDown,
  'gallery-horizontal': GalleryHorizontal,
  'unfold-vertical': UnfoldVertical,
  'toggle-right': ToggleRight,
  'menu': Menu,
  // Overlay
  'app-window': AppWindow,
  'alert-triangle': AlertTriangle,
  'message-square': MessageSquare,
  'info': Info,
  // Data
  'loader': Loader,
  'bar-chart-3': BarChart3,
}

// ---------------------------------------------------------------------------
// Section header component
// ---------------------------------------------------------------------------

function SectionHeader({
  icon: Icon,
  label,
  className,
}: {
  icon: LucideIcon
  label: string
  className?: string
}) {
  return (
    <div className={cn('flex items-center gap-1.5 px-2 py-1.5', className)}>
      <Icon className="h-3 w-3 text-muted-foreground" />
      <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
    </div>
  )
}

function getReadableElementLabel(id: string, type?: string): string {
  return /^el-[0-9a-f]{8}/.test(id) ? (type ?? id.slice(0, 11)) : id
}

function getBlockLabel(blocks: ReturnType<typeof useEditorStore.getState>['blocks'], blockName: string): string {
  return blocks[blockName]?.meta?.name ?? blockName
}

// ---------------------------------------------------------------------------
// InsertPanel
// ---------------------------------------------------------------------------

export function InsertPanel() {
  // Carryover: this panel's chrome was hardcoded English in a five-locale
  // product. Element TYPE labels ("Container", "Card") stay as they are —
  // they are the schema's own vocabulary, and renaming a type is a
  // product-language decision rather than a localization gap.
  const insertCopy = useStudioLocale().strings.insertPanel
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const app = useEditorStore((s) => s.app)
  const activePageId = useEditorStore((s) => s.activePageId)
  const blocks = useEditorStore((s) => s.blocks)
  const selectedIds = useEditorStore((s) => s.selectedIds)
  const selectedBlockName = useEditorStore((s) => s.selectedBlockName)
  const activePage = useMemo(() => {
    if (!app || !activePageId) return undefined
    return app.pages.find((p) => p.id === activePageId)
  }, [app, activePageId])

  // Auto-focus search on mount
  useEffect(() => {
    const timer = setTimeout(() => inputRef.current?.focus(), 50)
    return () => clearTimeout(timer)
  }, [])

  // Filter element items by search query
  const filteredCategories = useMemo(() => {
    const q = query.toLowerCase().trim()
    if (!q) return PALETTE_CATEGORIES

    return PALETTE_CATEGORIES.map((cat) => ({
      ...cat,
      items: cat.items.filter(
        (item) =>
          item.label.toLowerCase().includes(q) ||
          item.type.toLowerCase().includes(q),
      ),
    })).filter((cat) => cat.items.length > 0)
  }, [query])

  // Blocks from other pages (library)
  const libraryBlocks = useMemo(() => {
    if (!app || !activePageId) return []
    const activePage = app.pages.find((p) => p.id === activePageId)
    if (!activePage) return []
    const activeBlockSet = new Set(activePage.blocks)
    const items: { blockName: string; pageId: string }[] = []
    for (const page of app.pages) {
      if (page.id === activePageId) continue
      for (const blockName of page.blocks) {
        if (!activeBlockSet.has(blockName)) {
          items.push({ blockName, pageId: page.id })
        }
      }
    }
    return items
  }, [app, activePageId])

  // Filter library blocks by search
  const filteredLibraryBlocks = useMemo(() => {
    const q = query.toLowerCase().trim()
    if (!q) return libraryBlocks
    return libraryBlocks.filter(({ blockName }) => {
      const displayName = blocks[blockName]?.meta?.name ?? blockName
      return displayName.toLowerCase().includes(q) || blockName.toLowerCase().includes(q)
    })
  }, [query, libraryBlocks, blocks])

  const handleInsert = (type: string) => {
    const store = useEditorStore.getState()
    const { selectedIds, selectedBlockName, blocks: storeBlocks } = store

    let targetBlockName: string | null = selectedBlockName
    let parentId: string | null = null
    let insertIndex: number | undefined

    if (targetBlockName && storeBlocks[targetBlockName]) {
      const block = storeBlocks[targetBlockName]
      if (selectedIds.length > 0) {
        const selectedEl = findElementInTree(block.ui.root, selectedIds[0])
        if (selectedEl && isContainer(selectedEl)) {
          // Container selected → insert as child (at the end)
          parentId = selectedEl.id
        } else {
          // Non-container selected → insert as sibling AFTER it
          const parentInfo = findParentInTree(block.ui.root, selectedIds[0])
          if (parentInfo) {
            parentId = parentInfo.parent.id
            insertIndex = parentInfo.index + 1
          } else {
            parentId = block.ui.root.id
          }
        }
      } else {
        parentId = block.ui.root.id
      }
    } else {
      // No block selected → use last block on active page
      const activePage = store.getActivePage()
      if (activePage && activePage.blocks.length > 0) {
        targetBlockName = activePage.blocks[activePage.blocks.length - 1]
        const block = storeBlocks[targetBlockName]
        if (block) {
          parentId = block.ui.root.id
        }
      }
    }

    if (!targetBlockName || !parentId) return

    // Check maxChildren before inserting
    const block = storeBlocks[targetBlockName]
    if (block) {
      const parentEl = findElementInTree(block.ui.root, parentId)
      if (parentEl) {
        const currentCount = parentEl.children?.length ?? 0
        if (!canAddChild(parentEl.type, currentCount)) {
          const max = getMaxChildren(parentEl.type) ?? 0
          toast.warning(insertCopy.maxChildrenReached(currentCount, max))
          return
        }
      }
    }

    const newEl = createElement(type)
    store.addChildElement(targetBlockName, parentId, newEl, insertIndex)
    store.select(newEl.id, targetBlockName)
  }

  const handleCreateNewBlock = () => {
    const store = useEditorStore.getState()
    if (!store.activePageId) return
    store.createNewBlock(store.activePageId)
  }

  const handleAddLibraryBlock = (blockName: string) => {
    const store = useEditorStore.getState()
    if (!store.activePageId) return
    store.addBlockToPage(store.activePageId, blockName)
  }

  // Compute the insert target description for user feedback.
  const insertTarget = useMemo(() => {
    if (!activePageId) {
      return {
        title: insertCopy.noActivePage,
        detail: insertCopy.detailCreateOrOpenPage,
        tone: 'muted' as const,
      }
    }

    if (!selectedBlockName || !blocks[selectedBlockName] || selectedIds.length === 0) {
      const lastBlockName = activePage?.blocks.at(-1)
      if (!lastBlockName) {
        return {
          title: activePage?.title ?? activePageId,
          detail: insertCopy.detailNeedsBlockTarget,
          tone: 'muted' as const,
        }
      }
      return {
        title: getBlockLabel(blocks, lastBlockName),
        detail: insertCopy.detailAppendsToBlock,
        tone: 'ready' as const,
      }
    }

    const block = blocks[selectedBlockName]
    const selectedEl = findElementInTree(block.ui.root, selectedIds[0])
    if (selectedEl && isContainer(selectedEl)) {
      return {
        title: getReadableElementLabel(selectedIds[0], selectedEl.type),
        detail: insertCopy.detailInside(getBlockLabel(blocks, selectedBlockName)),
        tone: 'ready' as const,
      }
    }
    const parentInfo = findParentInTree(block.ui.root, selectedIds[0])
    if (parentInfo) {
      const parentLabel = getReadableElementLabel(parentInfo.parent.id, parentInfo.parent.type)
      const elLabel = getReadableElementLabel(selectedIds[0], selectedEl?.type)
      return {
        title: elLabel,
        detail: insertCopy.detailAfterSelectionIn(parentLabel),
        tone: 'ready' as const,
      }
    }
    return {
      title: getReadableElementLabel(selectedIds[0], selectedEl?.type),
      detail: insertCopy.detailInside(getBlockLabel(blocks, selectedBlockName)),
      tone: 'ready' as const,
    }
  }, [activePage, activePageId, selectedIds, selectedBlockName, blocks, insertCopy])

  const isSearchMode = query.trim().length > 0
  const canInsertElement = insertTarget.tone === 'ready'

  // Check if there are any results in search mode
  const hasLibraryResults = filteredLibraryBlocks.length > 0
  const hasElementResults = filteredCategories.length > 0
  const noResults = isSearchMode && !hasLibraryResults && !hasElementResults

  return (
    <div className="flex h-full flex-col bg-background" data-testid="insert-panel">
      <div className="border-b border-border px-3 py-3">
        <div
          data-testid="insert-target-card"
          className={cn(
            'mb-2 rounded-md border px-2.5 py-2',
            insertTarget.tone === 'ready'
              ? 'border-primary/30 bg-primary/5'
              : 'border-border bg-muted/30',
          )}
        >
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              {insertCopy.targetLabel}
            </span>
            <span className="truncate text-[10px] text-muted-foreground">
              {activePage?.route ?? activePageId ?? '-'}
            </span>
          </div>
          <div className="mt-1 min-w-0">
            <p className="truncate text-xs font-semibold text-foreground">
              {insertTarget.title}
            </p>
            <p className="truncate text-[11px] text-muted-foreground">
              {insertTarget.detail}
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleCreateNewBlock}
          disabled={!activePageId}
          className={cn(
            'mb-2 flex h-8 w-full items-center justify-center gap-2 rounded-md border text-xs font-medium transition-colors',
            activePageId
              ? 'border-border bg-background text-foreground hover:bg-muted'
              : 'cursor-not-allowed border-border bg-muted/30 text-muted-foreground/60',
          )}
          data-testid="insert-new-block"
        >
          <Plus className="h-3.5 w-3.5" />
          {insertCopy.newBlock}
        </button>

        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={insertCopy.searchPlaceholder}
            className="h-8 pl-7 text-xs"
            data-testid="insert-search"
          />
        </div>
      </div>

      {/* 3-tier content */}
      <ScrollArea className="flex-1">
        <div className="p-2">

          {/* ─── SECTION 1: FROM LIBRARY ─── */}
          {(hasLibraryResults || !isSearchMode) && (
            <div className="mb-3">
              <SectionHeader icon={Library} label={insertCopy.fromLibrary} />

              {libraryBlocks.length === 0 && !isSearchMode ? (
                <div className="px-2 py-2">
                  <p className="text-xs text-muted-foreground/60">
                    {insertCopy.noLibraryBlocks}
                  </p>
                </div>
              ) : (
                <Collapsible defaultOpen={filteredLibraryBlocks.length <= 6}>
                  <div className="space-y-0.5">
                    {filteredLibraryBlocks.slice(0, 3).map(({ blockName, pageId }) => (
                      <button
                        key={blockName}
                        onClick={() => handleAddLibraryBlock(blockName)}
                        className={cn(
                          'flex w-full items-center gap-2.5 rounded-xl px-2 py-1.5 text-left transition-colors',
                          'text-sm text-foreground/80 hover:bg-accent hover:text-foreground',
                        )}
                      >
                        <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-muted/50">
                          <Box className="h-4 w-4" />
                        </div>
                        <div className="flex flex-col min-w-0 flex-1">
                          <span className="truncate text-xs">{blocks[blockName]?.meta?.name ?? blockName}</span>
                          <span className="text-[10px] text-muted-foreground/50">{pageId}</span>
                        </div>
                      </button>
                    ))}
                  </div>

                  {filteredLibraryBlocks.length > 3 && (
                    <>
                      <CollapsibleTrigger
                        className={cn(
                          'flex w-full items-center gap-2 rounded-xl px-2 py-1 text-left transition-colors',
                          'text-xs text-muted-foreground hover:text-foreground',
                          '[&[data-state=open]>svg]:rotate-90',
                        )}
                      >
                        <ChevronRight className="h-3 w-3 transition-transform" />
                        <span>
                          {insertCopy.moreBlocks(filteredLibraryBlocks.length - 3)}
                        </span>
                      </CollapsibleTrigger>
                      <CollapsibleContent>
                        <div className="space-y-0.5">
                          {filteredLibraryBlocks.slice(3).map(({ blockName, pageId }) => (
                            <button
                              key={blockName}
                              onClick={() => handleAddLibraryBlock(blockName)}
                              className={cn(
                                'flex w-full items-center gap-2.5 rounded-xl px-2 py-1.5 text-left transition-colors',
                                'text-sm text-foreground/80 hover:bg-accent hover:text-foreground',
                              )}
                            >
                              <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-muted/50">
                                <Box className="h-4 w-4" />
                              </div>
                              <div className="flex flex-col min-w-0 flex-1">
                                <span className="truncate text-xs">{blocks[blockName]?.meta?.name ?? blockName}</span>
                                <span className="text-[10px] text-muted-foreground/50">{pageId}</span>
                              </div>
                            </button>
                          ))}
                        </div>
                      </CollapsibleContent>
                    </>
                  )}
                </Collapsible>
              )}
            </div>
          )}

          {/* ─── SECTION 2: CREATE MANUALLY ─── */}
          {(!isSearchMode || hasElementResults) && (
            <div className="mb-3">
              {!isSearchMode && <SectionHeader icon={Wrench} label={insertCopy.createManually} />}

              {/* Element catalog */}
              {filteredCategories.map((category) => (
                <div key={category.name} className="mb-2">
                  <div className="px-2 py-1 text-[10px] font-medium uppercase tracking-wider text-muted-foreground/60">
                    {category.name}
                  </div>
                  <div className="space-y-0.5">
                    {category.items.map((item) => {
                      const Icon = ICON_MAP[item.icon] || Square
                      return (
                        <button
                          key={item.type}
                          onClick={() => handleInsert(item.type)}
                          disabled={!canInsertElement}
                          className={cn(
                            'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                            canInsertElement
                              ? 'text-foreground/80 hover:bg-accent hover:text-foreground'
                              : 'cursor-not-allowed text-muted-foreground/50',
                          )}
                          data-testid={`insert-item-${item.type}`}
                        >
                          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-muted/50">
                            <Icon className="h-4 w-4" />
                          </div>
                          <span className="truncate text-xs">{item.label}</span>
                        </button>
                      )
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* No results */}
          {noResults && (
            <div className="flex flex-col items-center gap-2 py-8 text-center">
              <Search className="h-8 w-8 text-muted-foreground/40" />
              <p className="text-xs text-muted-foreground">{insertCopy.noElementsFound}</p>
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  )
}
